import type { Transaction } from '@finapp/types';
import type React from 'react';

interface TransactionCardViewProps {
  transactions: Transaction[];
  selectedItems: Set<number>;
  userRole: string;
  onEdit: (transaction: Transaction) => void;
  onDelete: (transactionId: number) => void;
  onSelect: (transactionId: number) => void;
}

const TransactionCardView: React.FC<TransactionCardViewProps> = ({
  transactions,
  selectedItems,
  userRole,
  onEdit,
  onDelete,
  onSelect,
}) => {
  return (
    <div className="lg:hidden space-y-4">
      {transactions.map((transaction) => (
        <div
          key={transaction.id}
          className={`bg-white rounded-lg shadow border ${
            selectedItems.has(transaction.id) ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
          }`}
        >
          <div className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                  checked={selectedItems.has(transaction.id)}
                  onChange={() => onSelect(transaction.id)}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {transaction.heading || transaction.original_heading || 'No heading'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(transaction.date).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onEdit(transaction)}
                  className="text-blue-600 hover:text-blue-900 text-sm"
                >
                  Edit
                </button>
                {(userRole === 'admin' || userRole === 'superadmin') && (
                  <button
                    onClick={() => onDelete(transaction.id)}
                    className="text-red-600 hover:text-red-900 p-1"
                    title="Delete transaction"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {transaction.description && (
              <div className="text-sm text-gray-600 mb-3 line-clamp-2">
                {transaction.description}
              </div>
            )}

            <div className="flex items-center justify-between mb-3">
              <span
                className={`text-lg font-semibold ${
                  transaction.type === 'deposit' ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {transaction.type === 'deposit' ? '+' : '-'}
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'EUR',
                  minimumFractionDigits: 2,
                }).format(Math.abs(transaction.amount))}
                {transaction.has_vat && <span className="text-xs text-gray-500 ml-1">(gross)</span>}
              </span>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  transaction.type === 'deposit'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {transaction.type === 'deposit' ? 'Income' : 'Expense'}
              </span>
            </div>

            {transaction.has_vat && (
              <div className="text-xs text-gray-600 mb-3 bg-gray-50 p-2 rounded">
                <div>
                  VAT (
                  {typeof transaction.vat_rate === 'object'
                    ? transaction.vat_rate.rate
                    : transaction.vat_rate}
                  %):{' '}
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'EUR',
                    minimumFractionDigits: 2,
                  }).format(transaction.vat_amount || 0)}
                </div>
                <div className="font-medium">
                  Net:{' '}
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'EUR',
                    minimumFractionDigits: 2,
                  }).format(transaction.net_amount || transaction.amount)}
                </div>
              </div>
            )}

            <div className="space-y-2">
              {transaction.costpoints.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-1">Costpoints:</div>
                  <div className="flex flex-wrap gap-1">
                    {transaction.costpoints.map((cp, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                      >
                        {cp.costpoint.toUpperCase()}: €{cp.amount.toFixed(2)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Show all tags: transaction-level and costpoint-level */}
              {(() => {
                // Debug log for Stripe transaction
                if (transaction.heading === 'Stripe Technology Europe Ltd') {
                  console.log('🔍 [Transaction List] Rendering Stripe transaction:', {
                    id: transaction.id,
                    transactionTags: transaction.tags,
                    costpoints: transaction.costpoints?.map((cp) => ({
                      name: cp.costpoint,
                      amount: cp.amount,
                      tags: cp.tags,
                    })),
                  });
                }

                // Collect all tags from transaction and costpoints
                const allTags = [...(transaction.tags || [])];

                // Debug log transaction tags
                if (transaction.heading === 'Stripe Technology Europe Ltd') {
                  console.log('🔍 [Transaction List] Transaction tags:', transaction.tags);
                }

                // Add costpoint-level tags
                transaction.costpoints?.forEach((costpoint) => {
                  // Debug log costpoint tags for Stripe
                  if (transaction.heading === 'Stripe Technology Europe Ltd') {
                    console.log(
                      `🔍 [Transaction List] Costpoint ${costpoint.costpoint} tags:`,
                      costpoint.tags
                    );
                  }

                  // Ensure costpoint.tags is treated as an array
                  const costpointTags = Array.isArray(costpoint.tags) ? costpoint.tags : [];

                  // Add each tag to allTags if it doesn't already exist
                  costpointTags.forEach((tag) => {
                    if (tag?.id && !allTags.find((existingTag) => existingTag.id === tag.id)) {
                      allTags.push(tag);
                    }
                  });
                });

                // Debug log all combined tags
                if (transaction.heading === 'Stripe Technology Europe Ltd') {
                  console.log('🔍 [Transaction List] All combined tags:', allTags);
                }

                return allTags.length > 0 ? (
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Tags:</div>
                    <div className="flex flex-wrap gap-1">
                      {allTags.map((tag, index) => (
                        <span
                          key={`${tag.id}-${index}`}
                          className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full"
                          title={tag.display_name}
                        >
                          {tag.display_name}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-gray-400">No tags</div>
                );
              })()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TransactionCardView;
